import React, {FC} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {AsyncHandlerOf, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {generateCopyValue} from '../../../helpers/customFieldHelpers';
import {
  addCustomAttribute,
  CardAttributeUpdateAction,
  deleteCustomAttribute,
  updateCustomAttribute,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {ContactDetailsSectionWithConflictsEnum} from '../../../helpers/models/cardMergeHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {CustomAttributeModel, CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {EditInCrmButtonContainer} from '../../common/crm/buttons/editInCrmButtonContainer';
import {CustomAttributeValue} from '../../common/customAttributes/customAttributeValue';
import {InlineEditingCustomAttribute} from '../../common/inlineEditing/inlineEditingCustomAttribute';
import {useContactDetailsContext} from '../contactDetailsContext';
import {ContactMergeConflictingValue} from '../contactMerge/contactMergeConflictingValue';
import {ContactInformationAttribute} from './contactInformationAttribute';

/*
 * Props.
 */

interface CustomAttributeProps {
  customAttribute: CustomAttributeModel;
  crmSyncEditLink?: string;
  card?: CardModel;
  teammates: ReadonlyArray<TeammateModel>;
  updateCardAttributeAsync: AsyncHandlerOf<CardAttributeUpdateAction>;
  updateUnsavedCard: HandlerOf<CardAttributeUpdateAction>;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

const StyledMergeConflictTeammateContainerDiv = styled.div`
  margin-top: 2px;
`;

const StyledContactMergeContainerDiv = styled.div`
  flex: 1;
  margin: 4px -4px 7px 0;
  overflow: hidden;
`;

const StyledContentDiv = styled.div`
  margin: 10px 0;
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

/*
 * Components.
 */

export const CustomAttribute: FC<CustomAttributeProps> = (props) => {
  const {customAttribute, teammates, crmSyncEditLink, card, updateCardAttributeAsync, updateUnsavedCard} =
    props;
  const isAddingNewCustomAttribute = customAttribute.value === '';
  const customFieldId = customAttribute.customField.id;

  const contactDetailsContext = useContactDetailsContext();

  // Check if we are merging and have a merge conflict
  const conflictingCustomAttributes =
    contactDetailsContext.mergeContext?.conflictingMergeValues?.[
      ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION
    ];
  const shouldShowMergeConflict = Boolean(
    conflictingCustomAttributes && conflictingCustomAttributes.some((value) => value.id === customFieldId),
  );

  if (shouldShowMergeConflict) {
    return (
      <StyledContentDiv {...buildInteractionIdProps(`contact-details-custom-attribute-${customFieldId}`)}>
        <StyledTitleDiv>{customAttribute.customField.name}</StyledTitleDiv>
        <StyledContactMergeContainerDiv>
          <ContactMergeConflictingValue
            section={ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION}
            customFieldId={customFieldId}
            renderOption={(value) => {
              if (typeof value !== 'string') {
                return value;
              }
              if (customAttribute.customField.type === CustomFieldsEnum.STRING) {
                return value;
              }
              if (customAttribute.customField.type === CustomFieldsEnum.TEAMMATE) {
                return (
                  <StyledMergeConflictTeammateContainerDiv>
                    <CustomAttributeValue customAttribute={customAttribute} editedValue={value} />
                  </StyledMergeConflictTeammateContainerDiv>
                );
              }
              return <CustomAttributeValue customAttribute={customAttribute} editedValue={value} />;
            }}
          />
        </StyledContactMergeContainerDiv>
      </StyledContentDiv>
    );
  }
  return (
    <ContactInformationAttribute
      key={customFieldId + customAttribute.value}
      interactionId={`contact-details-custom-attribute-${customFieldId}`}
      title={customAttribute.customField.name}
      currentAttributeValue={customAttribute.value}
      customEditButton={maybeRenderEditInCrmButton(crmSyncEditLink, card)}
      renderValue={(inlineEditingProps) => {
        if (inlineEditingProps.isEditing) {
          return (
            <InlineEditingCustomAttribute
              customAttribute={customAttribute}
              editedValue={inlineEditingProps.editedValue}
              onEdit={inlineEditingProps.onEdit}
              onEditSave={inlineEditingProps.onEditSave}
              onEditCancel={inlineEditingProps.onEditCancel}
            />
          );
        }

        if (isAddingNewCustomAttribute) {
          return <StyledValueDiv>-</StyledValueDiv>;
        }

        return (
          <StyledValueDiv>
            <CustomAttributeValue customAttribute={customAttribute} />
          </StyledValueDiv>
        );
      }}
      copyValue={generateCopyValue(customAttribute, teammates)}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(
          isAddingNewCustomAttribute,
          customAttribute.value,
          customAttribute.customField,
          newValue,
        );
        return updateCardAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteCustomAttribute(customAttribute.value, customAttribute.customField);

        // No need to hit the backend if the user deletes the attribute before it's saved
        if (isAddingNewCustomAttribute) {
          return updateUnsavedCard(action);
        }

        return updateCardAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingNewCustomAttribute}
      shouldHideCopyOption={customAttribute.customField.type === CustomFieldsEnum.BOOLEAN}
    />
  );
};

function computeOnSaveAction(
  isAddingNewCustomAttribute: boolean,
  currentValue: string,
  customField: CustomFieldModel,
  newValue: string,
) {
  if (isAddingNewCustomAttribute) {
    return addCustomAttribute(newValue, customField);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteCustomAttribute(currentValue, customField);
  }

  return updateCustomAttribute(currentValue, newValue, customField);
}

function maybeRenderEditInCrmButton(crmSyncEditLink?: string, card?: CardModel) {
  if (!crmSyncEditLink || !card || !card.externalInfo) {
    return null;
  }
  return <EditInCrmButtonContainer source={card.externalInfo.source} linkUrl={crmSyncEditLink} />;
}
